import { useMemo, useState } from 'react'
import type { EChartsCoreOption } from 'echarts/core'
import { formatMoney, type OptionStrategy } from '@greencandle/api-client'
import { useECharts } from './echarts'
import { useChartTheme } from './theme'

interface Pt {
  price: number
  pnl: number
}

/**
 * The server's corner points, plus its breakevens read in as zero-value corners.
 *
 * <p>A breakeven is not necessarily at a strike, so without this the profit/loss split below
 * would draw its boundary at the nearest corner rather than the exact crossing. Every value
 * here comes from the server; nothing is computed except where two points already agree the
 * line is at.
 */
function mergedPoints(strategy: OptionStrategy): Pt[] {
  const byPrice = new Map<number, number>()
  for (const p of strategy.payoffPoints) byPrice.set(Number(p.price), Number(p.pnl))
  for (const b of strategy.breakevens) {
    const price = Number(b)
    if (!byPrice.has(price)) byPrice.set(price, 0)
  }
  return [...byPrice.entries()].sort((a, b) => a[0] - b[0]).map(([price, pnl]) => ({ price, pnl }))
}

/** Linear interpolation between the two points either side of {@code price} - the payoff is
 *  exactly piecewise-linear, so this is exact, not an approximation. */
function pnlAt(points: Pt[], price: number): number | null {
  const first = points[0]
  const last = points[points.length - 1]
  if (!first || !last) return null
  if (price <= first.price) return first.pnl
  if (price >= last.price) return last.pnl

  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]
    const b = points[i + 1]
    if (!a || !b) continue
    if (price >= a.price && price <= b.price) {
      if (b.price === a.price) return a.pnl
      const t = (price - a.price) / (b.price - a.price)
      return a.pnl + t * (b.pnl - a.pnl)
    }
  }
  return null
}

/**
 * FR-H11 S6. The payoff-at-expiration curve for one strategy: profit above the zero line
 * shaded `--up`, loss below shaded `--down`, dashed verticals at each breakeven and at the
 * underlying's spot.
 *
 * <p>Draws only the server's corner points - no P&L math happens here beyond linear
 * interpolation for the tooltip, which is exact because the payoff is piecewise-linear by
 * construction (S1). The domain is S=0 to the highest strike; a strategy with unlimited
 * profit or loss past that is said in words on the card (`maxProfitUnbounded`/
 * `maxLossUnbounded`), not implied by extending the line into space this component would be
 * guessing at.
 */
export function PayoffDiagram({
  strategy,
  currency,
}: {
  strategy: OptionStrategy
  currency: string
}) {
  const theme = useChartTheme()
  const [showTable, setShowTable] = useState(false)

  const points = useMemo(() => mergedPoints(strategy), [strategy])
  const spotPrice = strategy.spot ? Number(strategy.spot.price) : null

  const option = useMemo<EChartsCoreOption | null>(() => {
    if (points.length < 2) return null

    // Two series covering the whole domain, each null everywhere the sign does not apply.
    // `connectNulls: false` turns that into a real gap, so each area only paints its own
    // side of the zero line - the split a single coloured series or a VisualMap (not
    // registered) would otherwise be needed for.
    const profit: [number, number | null][] = points.map((p) => [p.price, p.pnl >= 0 ? p.pnl : null])
    const loss: [number, number | null][] = points.map((p) => [p.price, p.pnl <= 0 ? p.pnl : null])

    // Breakeven values are already read off the stat tile below the chart, so the line here
    // carries no text of its own - a rotated label at every breakeven crowded the axis ticks
    // whenever a breakeven sat near the domain's edge, which is exactly where one usually is.
    const markLineData: Array<
      | { yAxis: number; lineStyle?: object; label?: object }
      | { xAxis: number; lineStyle?: object; label?: object }
    > = [
      { yAxis: 0, lineStyle: { color: theme.axis, width: 1, type: 'solid' }, label: { show: false } },
      ...strategy.breakevens.map((b) => ({
        xAxis: Number(b),
        lineStyle: { color: theme.textMuted, width: 1, type: 'dashed' as const },
        label: { show: false },
      })),
    ]
    if (spotPrice !== null) {
      markLineData.push({
        xAxis: spotPrice,
        lineStyle: { color: theme.accent, width: 1, type: 'dashed' as const },
        label: {
          formatter: () => `Spot${strategy.spot?.delayMinutes ? ' · 15-min delayed' : ''}`,
          color: theme.accent,
          backgroundColor: theme.surface,
          padding: [2, 4],
          fontSize: 11,
          rotate: 0,
          position: 'insideEndTop' as const,
        },
      })
    }

    return {
      animation: false,
      grid: { left: 8, right: 20, top: 24, bottom: 8, containLabel: true },
      xAxis: {
        type: 'value',
        scale: true,
        axisLine: { lineStyle: { color: theme.axis, width: 1 } },
        axisTick: { show: false },
        axisLabel: {
          color: theme.textMuted,
          hideOverlap: true,
          formatter: (value: number) => formatMoney(String(value), currency) ?? String(value),
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        scale: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: theme.textMuted,
          formatter: (value: number) => formatMoney(String(value), currency) ?? String(value),
        },
        splitLine: { lineStyle: { color: theme.grid, width: 1, type: 'solid' } },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line', snap: false, lineStyle: { color: theme.axis, width: 1 } },
        backgroundColor: theme.surface,
        borderColor: theme.grid,
        textStyle: { color: theme.text },
        formatter: (params: unknown) => {
          const list = Array.isArray(params) ? params : [params]
          const first = list[0] as { axisValue?: number } | undefined
          if (first?.axisValue === undefined) return ''
          const price = first.axisValue
          const pnl = pnlAt(points, price)
          if (pnl === null) return ''

          const root = document.createElement('div')
          const priceLine = document.createElement('div')
          priceLine.style.cssText = `color:${theme.textMuted};font-size:12px;margin-bottom:2px`
          priceLine.textContent = `At ${formatMoney(String(price), currency) ?? price}`
          root.append(priceLine)

          const pnlLine = document.createElement('div')
          pnlLine.style.cssText = `font-weight:600;color:${pnl >= 0 ? theme.up : theme.down}`
          pnlLine.textContent = formatMoney(String(pnl), currency) ?? String(pnl)
          root.append(pnlLine)
          return root
        },
      },
      series: [
        {
          name: 'Profit',
          type: 'line',
          data: profit,
          connectNulls: false,
          smooth: false,
          symbol: 'none',
          lineStyle: { width: 2, color: theme.up },
          areaStyle: { color: theme.up, opacity: 0.15 },
          markLine: {
            symbol: 'none',
            silent: true,
            data: markLineData,
          },
        },
        {
          name: 'Loss',
          type: 'line',
          data: loss,
          connectNulls: false,
          smooth: false,
          symbol: 'none',
          lineStyle: { width: 2, color: theme.down },
          areaStyle: { color: theme.down, opacity: 0.15 },
        },
      ],
    }
  }, [points, strategy.breakevens, spotPrice, strategy.spot?.delayMinutes, currency, theme])

  const container = useECharts(option)

  if (points.length < 2) {
    return null
  }

  return (
    <>
      <div className="chart-head">
        <div className="chart-head-end">
          <button type="button" onClick={() => setShowTable((v) => !v)} aria-pressed={showTable}>
            {showTable ? 'Chart' : 'Table'}
          </button>
        </div>
      </div>

      {showTable ? (
        <PayoffTable strategy={strategy} currency={currency} />
      ) : (
        <div ref={container} style={{ width: '100%', height: 220 }} />
      )}

      {strategy.spot == null && strategy.spotNote && (
        <p className="chart-note muted">Spot: {strategy.spotNote}</p>
      )}

      <p className="chart-note muted">
        Payoff at expiration. Options are unpriced (no provider quotes them), so no
        current-value curve is shown; stock legs use actual cost basis.
      </p>
    </>
  )
}

/** The table twin: every corner and breakeven the chart draws, reachable without hovering. */
function PayoffTable({ strategy, currency }: { strategy: OptionStrategy; currency: string }) {
  const points = mergedPoints(strategy)
  const breakevens = new Set(strategy.breakevens.map((b) => Number(b)))

  return (
    <div className="table-scroll" style={{ maxHeight: 220, overflowY: 'auto' }}>
      <table>
        <thead>
          <tr>
            <th className="right">Underlying price</th>
            <th className="right">P&amp;L</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {points.map((p) => (
            <tr key={p.price}>
              <td className="right num">{formatMoney(String(p.price), currency)}</td>
              <td className={`right num ${p.pnl >= 0 ? 'up' : 'down'}`}>
                {formatMoney(String(p.pnl), currency)}
              </td>
              <td className="muted">{breakevens.has(p.price) ? 'breakeven' : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
